import { makeObservable, observable, runInAction } from 'mobx'
import type { List } from 'app/generated/graphql'
import { SYSTEM_LIST_QUERY } from 'app/graphql/lists'
import { gqlQuery } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'

// Long enough that a page of table rows shares one read, short enough that a status added on the board shows soon.
const FRESH_MS = 60_000

/** System list records by id, for the contact surfaces that show one contact's place on each. */
export class SystemListStore {
  byId = new Map<string, List>()
  private readAt = new Map<string, number>()
  private inFlight = new Set<string>()

  constructor() {
    makeObservable(this, { byId: observable })
  }

  get(id: string): List | undefined {
    return this.byId.get(id)
  }

  ensure(id: string): void {
    const readAt = this.readAt.get(id)
    if (this.inFlight.has(id) || (readAt !== undefined && Date.now() - readAt < FRESH_MS)) return
    this.inFlight.add(id)
    gqlQuery(SYSTEM_LIST_QUERY, { id }, { requestPolicy: `network-only`, suppressErrorToast: true })
      .then(({ data, error }) => {
        // A failed read keeps the list already shown, and is retried after the freshness window.
        if (error || !data?.list) {
          logError(`Reading system list ${id} returned ${error ? `an error` : `no list`}`, error)
          return
        }
        runInAction(() => this.byId.set(id, data.list as List))
      })
      .catch((error) => logError(`Reading a system list failed`, error))
      .finally(() => {
        this.inFlight.delete(id)
        this.readAt.set(id, Date.now())
      })
  }
}
